import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// Icônes du menu existant (non modifiées)
import { LayoutDashboard, Trophy, Users, LogOut, User, Menu, X, CreditCard, ChevronDown, MessageSquare, FileText, Tag, MessageCircle, Clock, HeartHandshake, TrendingUp, Calendar, PlusCircle, QrCode, Building, Image, ShoppingBag, Package, ShoppingCart, Settings, BookOpen, Mail, Phone, Globe, Star, Heart, Home, Search, Stethoscope, Ticket, BarChart3, Percent } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useUser } from '../../hooks/useUser';
import { FooterSlot } from '../templates/FooterSlot';
import { SEOMetaTags } from '../shared/SEOMetaTags';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { useOrderNotifications } from '../../hooks/useOrderNotifications';
import { useTenantContext } from '../../context/TenantContext';
import { AdminHeaderSlot } from '../templates/AdminHeaderSlot';

// Mapping des icônes par nom
const iconMap: { [key: string]: any } = {
  FileText,
  Image,
  ShoppingBag,
  BookOpen,
  Building,
  MessageSquare,
  HeartHandshake,
  Users,
  Settings,
  LayoutDashboard,
  Tag,
  MessageCircle,
  PlusCircle,
  Package,
  ShoppingCart,
  User,
  Trophy,
  TrendingUp,
  Calendar,
  Mail,
  Phone,
  Globe,
  Star,
  Heart,
  Home,
  Search,
  Stethoscope,
  Ticket,
  BarChart3,
  Percent
};

// Configuration complète des menus avec sous-menus
const fullNavigationConfig: { [key: string]: any } = {
  blog: { 
    name: 'Blog', 
    href: '/admin/blog', 
    icon: FileText,
    submenu: [
      { name: 'Articles', href: '/admin/blog', icon: FileText },
      { name: 'Catégories', href: '/admin/blog/categories', icon: Tag },
      { name: 'Commentaires', href: '/admin/blog/comments', icon: MessageCircle },
    ]
  },
  portfolio: { 
    name: 'Portfolio', 
    href: '/admin/portfolio', 
    icon: Image,
    submenu: [
      { name: 'Toutes les œuvres', href: '/admin/portfolio', icon: Image },
      { name: 'Nouvelle œuvre', href: '/admin/portfolio/create', icon: PlusCircle },
      { name: 'Catégories', href: '/admin/portfolio/categories', icon: Tag },
    ]
  },
  boutique: { 
    name: 'Boutique', 
    href: '/admin/boutique', 
    icon: ShoppingBag,
    submenu: [
      { name: 'Produits', href: '/admin/boutique', icon: Package },
      { name: 'Commandes', href: '/admin/boutique/commandes', icon: ShoppingCart },
      { name: 'Catégories', href: '/admin/boutique/categories', icon: Tag },
    ]
  },
  elearning: { 
    name: 'E-Learning', 
    href: '/admin/elearning', 
    icon: BookOpen,
    submenu: [
      { name: 'Cours', href: '/admin/elearning', icon: BookOpen },
      { name: 'Avis', href: '/admin/elearning/reviews', icon: MessageSquare },
      { name: 'Inscriptions', href: '/admin/elearning/enrollments', icon: Users },
      { name: 'Catégories', href: '/admin/elearning/categories', icon: Tag },
      { name: 'Instructeurs', href: '/admin/elearning/instructors', icon: User },
      { name: 'Étudiants', href: '/admin/elearning/students', icon: Users },
    ]
  },
  partners: { name: 'Partenaires', href: '/admin/partners', icon: Building },
  messages: { name: 'Messages', href: '/admin/messages', icon: MessageSquare, badge: 'unreadCount' },
  emails: { name: 'Emails', href: '/admin/emails', icon: Mail },
  crowdfunding: { 
    name: 'Soutenir', 
    href: '/admin/crowdfunding', 
    icon: HeartHandshake,
    submenu: [
      { name: 'Campagnes', href: '/admin/crowdfunding', icon: HeartHandshake },
      { name: 'Créer une campagne', href: '/admin/crowdfunding/create', icon: Trophy },
      { name: 'Statistiques', href: '/admin/crowdfunding/stats', icon: TrendingUp },
    ]
  },
  santepsy: {
    name: 'Santé Psy',
    href: '/admin/psy/praticiens',
    icon: Stethoscope,
    submenu: [
      { name: 'Praticiens', href: '/admin/psy/praticiens', icon: Users },
      { name: 'Patients', href: '/admin/psy/patients', icon: User },
      { name: 'Rendez-vous', href: '/admin/psy/appointments', icon: Calendar },
      { name: 'Triage & Urgences', href: '/admin/psy/triage', icon: MessageSquare },
    ]
  },
  mediatheque: { name: 'Médiathèque', href: '/admin/mediatheque', icon: Image },
  coupons: {
    name: 'Coupons',
    href: '/admin/coupons',
    icon: Ticket,
    submenu: [
      { name: 'Tous les coupons', href: '/admin/coupons', icon: Ticket },
      { name: 'Nouveau coupon', href: '/admin/coupons/new', icon: PlusCircle },
    ]
  },
  clients: { name: 'Clients', href: '/admin/clients', icon: Users }, 
  configuration: { name: 'Configuration', href: '/admin/configuration', icon: Settings },
  dashboard: { name: 'Tableau de bord', href: '/admin/dashboard', icon: LayoutDashboard },
};

// Menus par défaut (fallback si pas de configuration personnalisée)
const defaultNavigation = [   
  fullNavigationConfig.blog,
  fullNavigationConfig.portfolio,
  fullNavigationConfig.boutique,
  fullNavigationConfig.elearning,
  fullNavigationConfig.santepsy,
  fullNavigationConfig.partners,
  fullNavigationConfig.messages,
  fullNavigationConfig.emails,
  fullNavigationConfig.crowdfunding,
  fullNavigationConfig.mediatheque,
  fullNavigationConfig.coupons,
  fullNavigationConfig.clients,
  fullNavigationConfig.configuration,
  fullNavigationConfig.dashboard,
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

// NOTE: La logique de ce hook est conservée telle quelle, avec ajout d'un breakpoint "ultra grand écran".
function useVisibleMenuItems(breakpoints = [1536, 1280, 1024, 768]) {
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      if (w >= breakpoints[0]) setVisibleCount(10);       // ≥ 1536px : ultra grand écran
      else if (w >= breakpoints[1]) setVisibleCount(6);  // [1280, 1535]
      else if (w >= breakpoints[2]) setVisibleCount(5);  // [1024, 1279]
      else if (w >= breakpoints[3]) setVisibleCount(3);  // [768, 1023]
      else setVisibleCount(2);                           // < 768
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoints]);

  return visibleCount;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { tenant, previewConfig } = useTenantContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [openOverflowSubmenu, setOpenOverflowSubmenu] = useState<string | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { unreadCount } = useUnreadMessages();
  const { pendingCount, paidCount } = useOrderNotifications();
  const visibleCount = useVisibleMenuItems();
  const [hasCustomAdminHeader, setHasCustomAdminHeader] = useState(false);
  const [customHeaderLoading, setCustomHeaderLoading] = useState(true);
  
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Si un preview est actif, on force l'affichage du header par défaut pour voir les changements en direct
  const showDefaultHeader = React.useMemo(() => {
    if (previewConfig) return true;
    return !hasCustomAdminHeader && !customHeaderLoading;
  }, [previewConfig, hasCustomAdminHeader, customHeaderLoading]);

  // Générer la navigation basée sur la configuration effective (prévisualisation > tenant)
  const navigation = React.useMemo(() => {
    const adminHeaderConfig = (previewConfig?.adminHeader) ?? (tenant?.config?.adminHeader);
    
    if (adminHeaderConfig?.menuItems) {
      // Vérifier si des menus système manquent dans la config (nouveaux menus ajoutés)
      const existingIds = adminHeaderConfig.menuItems.map((item: any) => item.id);
      const systemMenuIds = Object.keys(fullNavigationConfig);
      const missingMenus = systemMenuIds.filter(id => !existingIds.includes(id));
      
      // Fusionner les menus existants avec les nouveaux menus système (désactivés par défaut)
      let allMenuItems = [...adminHeaderConfig.menuItems];
      missingMenus.forEach(id => {
        const config = fullNavigationConfig[id];
        if (config) {
          allMenuItems.push({
            id,
            name: config.name,
            icon: config.icon?.name || 'FileText',
            enabled: false, // Désactivé par défaut, l'admin peut l'activer
            isCustom: false
          });
        }
      });
      
      // Utiliser la configuration personnalisée
      return allMenuItems
        .filter((item: any) => item.enabled)
        .map((item: any) => {
          const config = fullNavigationConfig[item.id];
          
          if (config) {
            // Menu système existant
            return { ...config, name: item.name };
          } else if (item.isCustom) {
            // Menu personnalisé - créer la configuration à la volée
            const IconComponent = iconMap[item.icon] || iconMap.FileText;
            return {
              name: item.name,
              href: item.href || `/admin/${item.id}`,
              icon: IconComponent,
              isCustom: true
            };
          }
          
          return null;
        })
        .filter(Boolean);
    }
    
    // Fallback sur la navigation par défaut
    return defaultNavigation;
  }, [previewConfig, tenant?.config?.adminHeader]);

  // Couleurs personnalisées du header
  const headerColors = React.useMemo(() => {
    const adminHeaderConfig = (previewConfig?.adminHeader) ?? (tenant?.config?.adminHeader);
    return {
      textColor: adminHeaderConfig?.textColor || '#ffffff',
      backgroundColor: adminHeaderConfig?.backgroundColor || '#002366',
      iconColor: adminHeaderConfig?.iconColor || '#D4AF37'
    };
  }, [previewConfig, tenant?.config?.adminHeader]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenSubmenu(null);
        setIsOverflowOpen(false);
        setOpenOverflowSubmenu(null);
        setIsProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOverflowOpen((v) => !v);
    setOpenOverflowSubmenu(null);
  };

  const handleOverflowParentClick = (itemName: string) => {
      setOpenOverflowSubmenu((current) => current === itemName ? null : itemName);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001122] to-[#000811] scrollbar-thin scrollbar-thumb-[#D4AF37]/50 scrollbar-track-white/5 hover:scrollbar-thumb-[#D4AF37]/70">
      <style dangerouslySetInnerHTML={{
        __html: `
          .scrollbar-thin::-webkit-scrollbar { width: 6px; height: 6px; }
          .scrollbar-track-white\/5::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); border-radius: 3px; }
          .scrollbar-thumb-[#D4AF37]\/50::-webkit-scrollbar-thumb { background-color: rgba(212, 175, 55, 0.5); border-radius: 3px; }
          .hover\:scrollbar-thumb-[#D4AF37]\/70:hover::-webkit-scrollbar-thumb { background-color: rgba(212, 175, 55, 0.7); }
          .scrollbar-thin { scrollbar-width: thin; scrollbar-color: rgba(212, 175, 55, 0.5) rgba(255, 255, 255, 0.05); }
        `
      }} />
      <SEOMetaTags
        title={`Administration - ${tenant?.name || 'SIS'}`}
        description={`Gestion de ${tenant?.name || 'votre site'} - Administration des cours, étudiants et contenus.`}
        url={`https://${tenant?.domain_name || window.location.hostname}/admin`}
      />
      {/* Slot de Header Admin (dynamique par domaine). Désactivé visuellement en mode preview pour afficher le header par défaut. */}
      <AdminHeaderSlot onStatusChange={(has, loading) => { setHasCustomAdminHeader(has); setCustomHeaderLoading(loading); }} />

      {showDefaultHeader && (
      <nav 
        className="fixed top-0 z-50 w-full backdrop-blur-md border-b border-opacity-20"
        style={{ 
          backgroundColor: `${headerColors.backgroundColor}90`,
          borderColor: `${headerColors.iconColor}33`
        }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center flex-1">
              <Link to="/admin/dashboard" className="flex-shrink-0 relative group flex items-center">
                <div className="relative mt-6">
                  <div 
                    className="relative h-24 w-24 rounded-full border-2 border-opacity-30 flex items-center justify-center overflow-hidden"
                    style={{ 
                      backgroundColor: headerColors.backgroundColor,
                      borderColor: `${headerColors.iconColor}4D`
                    }}
                  >
                    {tenant?.logo ? (
                      <img 
                        className="h-20 w-20 object-cover rounded-full transition-all duration-500 group-hover:scale-105" 
                        src={tenant.logo} 
                        alt={`Logo ${tenant?.name || 'Administration'}`}
                      />
                    ) : (
                      <span 
                        className="font-serif text-4xl font-bold"
                        style={{ color: headerColors.iconColor }}
                      >
                        {tenant?.name ? tenant.name.substring(0, 2).toUpperCase() : 'EC'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4 hidden lg:block">
                  <h1 className="text-xl font-bold">
                    <span 
                      className="bg-gradient-to-r text-transparent bg-clip-text"
                      style={{ 
                        backgroundImage: `linear-gradient(to right, ${headerColors.textColor}, ${headerColors.iconColor})`
                      }}
                    >
                      {tenant?.name || 'Site'}
                    </span>
                  </h1>
                  <p 
                    className="text-xs opacity-70"
                    style={{ color: headerColors.iconColor }}
                  >
                    Administration
                  </p>
                </div>
              </Link>

              {/* Menu Desktop Responsive avec icônes colorées */}
              <div className="hidden md:flex items-center space-x-4 ml-auto" ref={menuRef}>
                {(() => {
                  const visibleItems = navigation.slice(0, visibleCount);
                  const overflowItems = navigation.slice(visibleCount);
                  
                  return (
                    <>
                      {visibleItems.map((item: any) =>
                        !item.submenu ? (
                          <Link 
                            key={item.href} 
                            to={item.href}
                            className={`relative transition-colors flex items-center whitespace-nowrap p-2 rounded-md ${location.pathname.startsWith(item.href) ? 'bg-opacity-10' : ''}`}
                            style={{ 
                              color: location.pathname.startsWith(item.href) ? headerColors.iconColor : headerColors.textColor,
                              backgroundColor: location.pathname.startsWith(item.href) ? `${headerColors.iconColor}1A` : 'transparent'
                            }}
                          >
                            {/* Icône colorée */}
                            <div>
                              {item.icon && <item.icon className="h-5 w-5 mr-1.5" style={{ color: headerColors.iconColor }} />}
                              {item.name === 'Messages' && unreadCount > 0 && (
                                <span 
                                  className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs rounded-full h-4 w-4 flex items-center justify-center"
                                  style={{ 
                                    backgroundColor: headerColors.iconColor,
                                    color: headerColors.backgroundColor
                                  }}
                                >
                                  {unreadCount}
                                </span>
                              )}
                              {item.name === 'Boutique' && (pendingCount > 0 || paidCount > 0) && (
                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex">
                                  {pendingCount > 0 && (
                                    <span className="bg-yellow-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center mr-1">
                                      {pendingCount}
                                    </span>
                                  )}
                                  {paidCount > 0 && (
                                    <span className="bg-green-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                      {paidCount}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {item.name}
                          </Link>
                        ) : (
                          <div key={item.name} className="relative group">
                            <div 
                              className={`transition-colors flex items-center whitespace-nowrap p-2 rounded-md relative cursor-pointer ${location.pathname.startsWith(item.href) ? 'bg-opacity-10' : ''}`}
                              style={{ 
                                color: location.pathname.startsWith(item.href) ? headerColors.iconColor : headerColors.textColor,
                                backgroundColor: location.pathname.startsWith(item.href) ? `${headerColors.iconColor}1A` : 'transparent'
                              }}
                              onClick={() => setOpenSubmenu(openSubmenu === item.name ? null : item.name)}
                            >
                              {/* Icône colorée */}
                              <div>
                                {item.icon && <item.icon className="h-5 w-5 mr-1.5" style={{ color: headerColors.iconColor }} />}
                                {item.name === 'Messages' && unreadCount > 0 && (
                                  <span 
                                    className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs rounded-full h-4 w-4 flex items-center justify-center"
                                    style={{ 
                                      backgroundColor: headerColors.iconColor,
                                      color: headerColors.backgroundColor
                                    }}
                                  >
                                    {unreadCount}
                                  </span>
                                )}
                                {item.name === 'Boutique' && (pendingCount > 0 || paidCount > 0) && (
                                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex">
                                    {pendingCount > 0 && (
                                      <span className="bg-yellow-500 text-xs rounded-full h-4 w-4 flex items-center justify-center mr-1" style={{ color: headerColors.backgroundColor }}>
                                        {pendingCount}
                                      </span>
                                    )}
                                    {paidCount > 0 && (
                                      <span className="bg-green-500 text-xs rounded-full h-4 w-4 flex items-center justify-center" style={{ color: headerColors.backgroundColor }}>
                                        {paidCount}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {item.name}
                              <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openSubmenu === item.name ? 'rotate-180' : ''}`} />
                            </div>
                            
                            {openSubmenu === item.name && (
                              <div 
                                className="absolute left-0 mt-2 w-56 rounded-md shadow-lg backdrop-blur-sm border border-opacity-20 z-50"
                                style={{ 
                                  backgroundColor: `${headerColors.backgroundColor}E6`,
                                  borderColor: `${headerColors.iconColor}33`
                                }}
                              >
                                <div className="py-1">
                                  {item.submenu.map((sub: any) => (
                                    <Link
                                      key={sub.href}
                                      to={sub.href}
                                      className={`block px-4 py-2 text-sm transition-colors ${location.pathname === sub.href ? '' : ''}`}
                                      style={{ 
                                        color: location.pathname === sub.href ? headerColors.iconColor : headerColors.textColor,
                                        backgroundColor: location.pathname === sub.href ? `${headerColors.iconColor}1A` : 'transparent'
                                      }}
                                      onClick={() => setOpenSubmenu(null)}
                                    >
                                      {/* Icône de sous-menu colorée */}
                                      {sub.icon && <sub.icon className="h-4 w-4 mr-2 inline opacity-80" style={{ color: headerColors.iconColor }} />}
                                      {sub.name}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      )}
                      
                      {overflowItems.length > 0 && (
                        <div className="relative group">
                          <div 
                            className="flex items-center px-3 py-2 text-sm text-gray-200 hover:text-white cursor-pointer rounded-md hover:bg-white/5"
                            onClick={handleMenuClick}
                          >
                            <ChevronDown className="h-4 w-4 ml-1" style={{ color: headerColors.iconColor }} />
                          </div>
                          {isOverflowOpen && (
                            <div
                              className="absolute right-0 mt-2 w-56 origin-top-right rounded-md backdrop-blur-lg border shadow-lg z-50"
                              style={{
                                backgroundColor: `${headerColors.backgroundColor}E6`,
                                borderColor: `${headerColors.iconColor}33`
                              }}
                            >
                              <div className="py-1">
                                {overflowItems.map(item => (
                                  <React.Fragment key={item.name}>
                                    {!item.submenu ? (
                                       <Link
                                         to={item.href}
                                         className="relative flex items-center px-3 py-2 rounded-md text-sm font-medium"
                                         style={{
                                           color: location.pathname.startsWith(item.href) ? headerColors.iconColor : headerColors.textColor,
                                           backgroundColor: location.pathname.startsWith(item.href) ? `${headerColors.iconColor}1A` : 'transparent'
                                         }}
                                         onClick={() => setIsOverflowOpen(false)}
                                       >
                                         {/* Icône colorée */}
                                         <div>
                                           {item.icon && <item.icon className="h-5 w-5 mr-2 inline" style={{ color: headerColors.iconColor }} />}
                                           {item.badge && unreadCount > 0 && (
                                             <span
                                               className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs rounded-full h-4 w-4 flex items-center justify-center"
                                               style={{ backgroundColor: headerColors.iconColor, color: headerColors.backgroundColor }}
                                             >
                                               {unreadCount}
                                             </span>
                                           )}
                                           {item.name === 'Boutique' && (pendingCount > 0 || paidCount > 0) && (
                                             <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex">
                                               {pendingCount > 0 && (
                                                 <span className="bg-yellow-500 text-xs rounded-full h-4 w-4 flex items-center justify-center mr-1" style={{ color: headerColors.backgroundColor }}>
                                                   {pendingCount}
                                                 </span>
                                               )}
                                               {paidCount > 0 && (
                                                 <span className="bg-green-500 text-xs rounded-full h-4 w-4 flex items-center justify-center" style={{ color: headerColors.backgroundColor }}>
                                                   {paidCount}
                                                 </span>
                                               )}
                                             </div>
                                           )}
                                         </div>
                                         {item.name}
                                        </Link>
                                      ) : (
                                        <div>
                                          <button
                                            onClick={() => handleOverflowParentClick(item.name)}
                                            className="w-full text-left px-4 py-2 text-sm flex items-center justify-between rounded-md"
                                            style={{ color: headerColors.textColor, backgroundColor: 'transparent' }}
                                          >
                                            <span className="flex items-center">
                                              {/* Icône colorée */}
                                              {item.icon && <item.icon className="h-4 w-4 mr-2" style={{ color: headerColors.iconColor }} />} {item.name}
                                            </span>
                                            <ChevronDown className={`h-4 w-4 transition-transform ${openOverflowSubmenu === item.name ? 'rotate-180' : ''}`} style={{ color: headerColors.iconColor }} />
                                          </button>
                                          {openOverflowSubmenu === item.name && (
                              <div
                                className="absolute top-full left-0 mt-1 w-48 backdrop-blur-sm border rounded-md shadow-lg z-10"
                                style={{ backgroundColor: `${headerColors.backgroundColor}E6`, borderColor: `${headerColors.iconColor}33` }}
                              >
                                {item.submenu.map(sub => (
                                  <Link
                                    key={sub.href}
                                    to={sub.href}
                                    className={`block px-4 py-2 text-sm first:rounded-t-md last:rounded-b-md`}
                                    style={{
                                      color: location.pathname === sub.href ? headerColors.iconColor : headerColors.textColor,
                                      backgroundColor: location.pathname === sub.href ? `${headerColors.iconColor}1A` : 'transparent'
                                    }}
                                    onClick={() => setIsOverflowOpen(false)}
                                  >
                                    {/* Icône de sous-menu colorée */}
                                    {sub.icon && <sub.icon className="h-4 w-4 mr-2 inline" style={{ color: headerColors.iconColor }} />}
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                                        </div>
                                      )}
                                    </React.Fragment>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <button 
                        onClick={handleLogout} 
                        className="px-4 py-2 rounded-full font-medium transition-all ml-4 text-sm"
                        style={{
                          background: `linear-gradient(to right, ${headerColors.iconColor}, ${headerColors.iconColor}CC)`,
                          color: headerColors.backgroundColor
                        }}
                      >
                        Déconnexion
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div className="flex items-center md:hidden">
              <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="focus:outline-none"
                style={{ color: headerColors.textColor }}
              >
                {isOpen ? 
                  <X className="h-6 w-6" /> : 
                  <Menu className="h-6 w-6" style={{ color: headerColors.iconColor }} />
                }
              </button>
            </div>
            
            {/* Menu Mobile avec icônes colorées */}
            {isOpen && (
               <div 
                 className="md:hidden absolute top-20 inset-x-0 backdrop-blur-sm border-b border-opacity-20"
                 style={{ 
                   backgroundColor: `${headerColors.backgroundColor}F2`,
                   borderColor: `${headerColors.iconColor}33`
                 }}
               >
                <div className="px-2 pt-2 pb-3 space-y-1">
                  {navigation.map((item: any) => (
                    <React.Fragment key={item.name}>
                       <Link 
                         to={item.href} 
                         onClick={() => setIsOpen(false)} 
                         className="relative flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors"
                         style={{ 
                           color: headerColors.textColor,
                           backgroundColor: 'transparent'
                         }}
                       >
                        {/* Icône colorée */}
                        <div>
                           {item.icon && <item.icon className="h-5 w-5 mr-3 inline" style={{ color: headerColors.iconColor }} />}
                          {item.name === 'Messages' && unreadCount > 0 && (
                            <span 
                              className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs rounded-full h-4 w-4 flex items-center justify-center"
                              style={{ 
                                backgroundColor: headerColors.iconColor,
                                color: headerColors.backgroundColor
                              }}
                            >
                              {unreadCount}
                            </span>
                          )}
                          {item.name === 'Boutique' && (pendingCount > 0 || paidCount > 0) && (
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex">
                              {pendingCount > 0 && (
                                <span className="bg-yellow-500 text-xs rounded-full h-4 w-4 flex items-center justify-center mr-1" style={{ color: headerColors.backgroundColor }}>
                                  {pendingCount}
                                </span>
                              )}
                              {paidCount > 0 && (
                                <span className="bg-green-500 text-xs rounded-full h-4 w-4 flex items-center justify-center" style={{ color: headerColors.backgroundColor }}>
                                  {paidCount}
                                </span>
                              )}
                            </div>
                          )}
                        </div> {item.name}
                      </Link>
                      {item.submenu && (
                        <div className="pl-5 space-y-1">
                          {item.submenu.map((submenuItem: any) => (
                             <Link 
                               key={submenuItem.name} 
                               to={submenuItem.href} 
                               onClick={() => setIsOpen(false)} 
                               className="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors"
                               style={{ 
                                 color: `${headerColors.textColor}CC`,
                                 backgroundColor: 'transparent'
                               }}
                             >
                              {/* Icône de sous-menu colorée */}
                               {submenuItem.icon && <submenuItem.icon className="h-4 w-4 mr-2 inline opacity-80" style={{ color: headerColors.iconColor }} />} {submenuItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                   <div 
                     className="border-t border-opacity-20 mt-4 pt-4"
                     style={{ borderColor: `${headerColors.iconColor}33` }}
                   >
                     <button 
                       onClick={() => { setIsOpen(false); handleLogout(); }} 
                       className="w-full text-left flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors"
                       style={{ 
                         color: headerColors.textColor,
                         backgroundColor: 'transparent'
                       }}
                     >
                       {/* Icône colorée */}
                       <LogOut className="h-5 w-5 mr-3 inline" style={{ color: headerColors.iconColor }} />Déconnexion
                     </button>
                   </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
      )}

      <main className="pt-20 flex-1">
        <div className="max-w-8xl mx-auto py-6 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      <FooterSlot />
    </div>
  );
};